import React, { useState } from 'react';
import {
  User, Mail, Lock, Eye, EyeOff, LogOut, Save, Shield, Crown,
  Globe, Clock, Camera, ChevronRight, Compass
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { NoahExpressionCard } from '../components/NoahExpressionCard';
import { TourProgressCard } from '../components/TourProgressCard';
import { ExplorerLeaderboard } from '../components/ExplorerLeaderboard';
import { StickerStudio } from '../components/StickerStudio';
import { MorningDigestCard } from '../components/MorningDigestCard';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const roleLabels = { admin: 'Administrador', manager: 'Manager', monitor: 'Monitor', viewer: 'Viewer' };
const roleColors = { admin: 'text-amber-700 dark:text-amber-400 bg-amber-500/10 border-amber-500/20', manager: 'text-brand-deep dark:text-brand-deep dark:text-brand-green bg-violet-500/10 border-violet-500/20', monitor: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20' };

const Profile = () => {
  const { user, logout, updateProfile } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    language: user?.language || 'es',
  });
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Mínimo 6 caracteres');
      return;
    }
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new_password
      });
      toast.success('Contraseña actualizada');
      setPasswordForm({ current: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="relative group">
          <Avatar className="h-20 w-20 ring-4 ring-violet-500/30">
            <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
            <Camera className="w-5 h-5 text-white" />
          </div>
        </div>
        <div>
          <h1 className="text-2xl font-bold">{user?.name}</h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <Badge variant="outline" className={cn("text-xs mt-1", roleColors[user?.role] || '')}>
            <Shield className="w-3 h-3 mr-1" />
            {roleLabels[user?.role] || user?.role}
          </Badge>
        </div>
      </div>

      {/* Basic Info */}
      <Card className="bg-card/80 backdrop-blur border-white/10">
        <CardContent className="p-5 space-y-4">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <User className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" /> Informacion Personal
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nombre</Label>
              <Input data-testid="profile-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="profile-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} type="email" />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input data-testid="profile-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+57 300 1234567" />
            </div>
            <div>
              <Label>Idioma</Label>
              <Select value={form.language} onValueChange={v => setForm({...form, language: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">Espanol</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Tema</Label>
            <div className="flex gap-2 mt-1">
              {Object.entries(themes || {}).map(([key, t]) => (
                <button key={key} onClick={() => setTheme(key)} className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border",
                  theme === key ? "border-violet-500 bg-violet-500/10 text-brand-deep dark:text-brand-deep dark:text-brand-green" : "border-border hover:border-muted-foreground/30"
                )}>
                  {t.label || key}
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="save-profile-btn" className="bg-primary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-card/80 backdrop-blur border-white/10">
        <CardContent className="p-5 space-y-4">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-700 dark:text-amber-400" /> Cambiar Contraseña
          </h2>
          <div className="space-y-3">
            <div>
              <Label>Contraseña Actual</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="current-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordForm.current}
                  onChange={e => setPasswordForm({...passwordForm, current: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nueva Contraseña</Label>
                <Input
                  data-testid="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <Label>Confirmar</Label>
                <Input
                  data-testid="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button data-testid="change-password-btn" variant="outline" className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                onClick={handleChangePassword} disabled={!passwordForm.current || !passwordForm.new_password}>
                Cambiar Contraseña
              </Button>
              <button className="text-xs text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                {showPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Info */}
      <Card className="bg-card/80 backdrop-blur border-white/10">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-brand-deep dark:text-brand-green" /> Sesion
          </h2>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Ultimo acceso: {user?.last_login ? new Date(user.last_login).toLocaleString('es') : 'Ahora'}</p>
            <p>Registrado: {user?.created_at ? new Date(user.created_at).toLocaleDateString('es') : '-'}</p>
          </div>
          <Button
            data-testid="logout-btn"
            variant="destructive"
            className="w-full mt-4"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion
          </Button>
        </CardContent>
      </Card>

      <TourProgressCard />

      <ExplorerLeaderboard />

      <StickerStudio />

      <MorningDigestCard />

      <NoahExpressionCard />

      <NoahVoicePanel />
    </div>
  );
};

// ============ Noah Voice Cloning Panel ============
const NoahVoicePanel = () => {
  const [samples, setSamples] = useState([]);
  const [text, setText] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [testText, setTestText] = useState('');
  const [score, setScore] = useState(null);
  const [scoring, setScoring] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/noah/voice-samples`)
      .then(({ data }) => { if (!cancelled) setSamples(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [reloadKey]);

  const add = async () => {
    if (!text.trim() || text.trim().length < 10) {
      toast.error('Minimo 10 caracteres');
      return;
    }
    try {
      await axios.post(`${API}/noah/voice-samples`, { text: text.trim() });
      toast.success('Ejemplo guardado · Noah ya imita tu estilo');
      setText('');
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/noah/voice-samples/${id}`);
      toast.success('Eliminado');
      setReloadKey(k => k + 1);
    } catch { toast.error('Error'); }
  };

  const runScore = async () => {
    if (!testText.trim() || testText.trim().length < 5) {
      toast.error('Escribe al menos 5 caracteres');
      return;
    }
    setScoring(true);
    try {
      const { data } = await axios.post(`${API}/noah/voice-score`, { text: testText.trim() });
      setScore(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setScoring(false);
    }
  };

  const scoreColor = score?.score >= 85 ? 'text-emerald-700 dark:text-emerald-400' : score?.score >= 70 ? 'text-amber-700 dark:text-amber-400' : score?.score >= 50 ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400';

  return (
    <Card className="bg-card/80 backdrop-blur border-white/10" data-testid="noah-voice-panel">
      <CardContent className="p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          ✨ Clona tu voz en Noah
        </h2>
        <p className="text-xs text-muted-foreground">
          Pega 3-5 ejemplos cortos de mensajes que TU enviarias a un cliente. Noah copiara tu tono, expresiones y muletillas.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Ej: '¡Ey parce! Mira, lo que te recomiendo es que cojas el plan premium, es una chimba la verdad...'"
          className="w-full px-3 py-2 rounded-md border bg-background text-sm"
          data-testid="voice-sample-input"
        />
        <Button onClick={add} className="w-full bg-primary text-primary-foreground" data-testid="add-voice-sample-btn">
          Agregar ejemplo
        </Button>
        <div className="space-y-2 max-h-64 overflow-y-auto pt-2">
          {samples.length === 0 && (
            <p className="text-xs text-muted-foreground italic">Sin ejemplos aun. Noah usara su tono por defecto.</p>
          )}
          {samples.map(s => (
            <div key={s.id} className="flex items-start gap-2 p-2 rounded bg-muted/40 text-xs" data-testid={`voice-sample-${s.id}`}>
              <p className="flex-1 italic">&ldquo;{s.text}&rdquo;</p>
              <button onClick={() => remove(s.id)} className="text-red-600 dark:text-red-400 hover:text-red-700" data-testid={`remove-voice-${s.id}`}>×</button>
            </div>
          ))}
        </div>

        {/* Voice Score Preview */}
        <div className="pt-4 border-t border-white/10 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prueba de tono</h3>
          <p className="text-[11px] text-muted-foreground">Escribe una respuesta y mira que tan parecida suena a ti.</p>
          <textarea
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            rows={2}
            placeholder="Ej: 'Claro que si parcero, esa promo es una nota. Te la dejo lista.'"
            className="w-full px-3 py-2 rounded-md border bg-background text-sm"
            data-testid="voice-score-input"
          />
          <Button onClick={runScore} disabled={scoring} variant="outline" className="w-full" data-testid="voice-score-btn">
            {scoring ? 'Analizando...' : 'Evaluar tono'}
          </Button>
          {score && (
            <div className="p-3 rounded-lg bg-muted/30 border border-white/10" data-testid="voice-score-result">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Suena como tú</span>
                <span className={`text-2xl font-bold ${scoreColor}`}>{score.score}%</span>
              </div>
              <p className="text-xs mt-1">{score.feedback}</p>
              {score.matched_terms?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {score.matched_terms.slice(0, 8).map(t => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-brand-deep dark:text-brand-deep dark:text-brand-green">{t}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default Profile;
